import { useState, useEffect, useCallback } from 'react';
import api from '../../../services/api';
import toast from 'react-hot-toast';

const RevenueConfigPage = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/revenue-config');
      if (response.success) {
        setConfigs(response.data.configs);
      } else {
        toast.error(response.message || 'Failed to fetch configurations');
      }
    } catch (error) {
      console.error('Error fetching configs:', error);
      toast.error(error.message || 'Failed to fetch configurations');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleEdit = (config) => {
    setEditingId(config.id);
    // Convert decimal rates to percentages for display (0.20 -> 20)
    // If value is already > 1, it's stored as percentage, don't multiply
    let displayValue = config.config_value;
    if (config.config_key.includes('RATE') || config.config_key === 'FX_MARKUP') {
      displayValue = config.config_value > 1 ? config.config_value : config.config_value * 100;
    }
    setEditValues({
      config_value: displayValue,
      description: config.description,
    });
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditValues({});
  };

  const handleSave = async (configKey) => {
    try {
      setSaving(true);
      // For FX_MARKUP, save as-is (backend expects percentage value like 5, not 0.05)
      // For other RATE configs, convert percentage to decimal (20 -> 0.20)
      let saveValue = editValues.config_value;
      if (configKey === 'FX_MARKUP') {
        // FX_MARKUP is stored as percentage in backend (5 not 0.05)
        saveValue = editValues.config_value;
      } else if (configKey.includes('RATE')) {
        // Other rates are stored as decimals (0.20 not 20)
        saveValue = editValues.config_value / 100;
      }
      
      const response = await api.put(
        `/admin/revenue-config/${configKey}`,
        {
          config_value: saveValue,
          description: editValues.description
        }
      );

      if (response.success) {
        toast.success(response.message || 'Configuration updated successfully');
        await fetchConfigs();
        setEditingId(null);
        setEditValues({});
        
        // If FX_MARKUP was changed, broadcast to all tabs and clear cache
        if (configKey === 'FX_MARKUP') {
          console.log('💱 [RevenueConfig] FX_MARKUP changed, broadcasting update...');
          
          // Clear sessionStorage cache
          sessionStorage.removeItem('fx_preference_cache');
          
          // Broadcast to all tabs
          if (typeof BroadcastChannel !== 'undefined') {
            const channel = new BroadcastChannel('fx_updates');
            channel.postMessage({ 
              type: 'MARKUP_CHANGED', 
              timestamp: Date.now(),
              newValue: saveValue 
            });
            channel.close();
          }
          
          toast.success('FX markup updated! All users will see new rates within 5 minutes.', { duration: 5000 });
        }
      } else {
        toast.error(response.message || 'Failed to save configuration');
      }
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error(error.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setEditValues({
      ...editValues,
      [field]: value,
    });
  };

  const formatValue = (key, value) => {
    if (key.includes('RATE') || key === 'FX_MARKUP') {
      // If value is already > 1, it's stored as percentage, don't multiply
      const percentValue = value > 1 ? value : value * 100;
      return `${percentValue.toFixed(2)}%`;
    }
    return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
  };

  if (loading) {
    return (
      <div className="p-12 text-center">
        <i className="fas fa-spinner fa-spin text-2xl text-gray-400 mb-2"></i>
        <p className="text-gray-600">Loading configurations...</p>
      </div>
    );
  }

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-6 text-gray-800">Revenue Configuration</h1>

      <div className="bg-white rounded-lg shadow">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-200 border-b-2 border-gray-300">
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">#</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Configuration</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Value</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Description</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Updated</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Action</th>
              </tr>
            </thead>
            <tbody>
              {configs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                    No configurations found
                  </td>
                </tr>
              ) : (
                configs.map((config, index) => (
                  <tr key={config.id} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-700">{index + 1}</td>
                    <td className="px-6 py-4 text-sm font-mono text-gray-700">{config.config_key}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {editingId === config.id ? (
                        <input
                          type="number"
                          step="0.01"
                          min="1"
                          max="100"
                          value={editValues.config_value || ''}
                          onChange={(e) => {
                            const value = e.target.value === '' ? '' : parseFloat(e.target.value);
                            if (value === '' || (!isNaN(value) && value >= 1 && value <= 100)) {
                              handleInputChange('config_value', value);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === '-' || e.key === 'e' || e.key === 'E' || e.key === '+') {
                              e.preventDefault();
                            }
                          }}
                          className="w-32 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="1-100"
                        />
                      ) : (
                        <span className="font-semibold">{formatValue(config.config_key, config.config_value)}</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {editingId === config.id ? (
                        <textarea
                          value={editValues.description || ''}
                          onChange={(e) =>
                            handleInputChange('description', e.target.value)
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          rows="2"
                        />
                      ) : (
                        <span>{config.description || '-'}</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                      {new Date(config.updated_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {editingId === config.id ? (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleSave(config.config_key)}
                            disabled={saving}
                            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 font-medium"
                          >
                            Save
                          </button>
                          <button
                            onClick={handleCancel}
                            disabled={saving}
                            className="px-4 py-2 bg-gray-400 text-white rounded hover:bg-gray-500 disabled:opacity-50 font-medium"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleEdit(config)}
                          className="px-4 py-2 bg-jet-yellow text-black rounded hover:bg-jet-yellow font-medium"
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RevenueConfigPage;
